import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { API_ORIGIN, authAPI, complaintAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { normalizePhoneInput } from '../../utils/validation';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './ComplaintsPage.css';

const MAX_FILES = 5;
const IMAGE_LIMIT = 5 * 1024 * 1024;
const VIDEO_LIMIT = 25 * 1024 * 1024;
const TOTAL_LIMIT = 50 * 1024 * 1024;

const CATEGORY_CONFIG = {
  water: { label: 'पाणीपुरवठा', icon: '💧', hint: 'नळ, पाईपलाईन, टाकी, दूषित पाणी' },
  road: { label: 'रस्ता', icon: '🛣️', hint: 'खड्डे, चिखल, रस्ता दुरुस्ती' },
  electricity: { label: 'वीज', icon: '⚡', hint: 'वीज खांब, तारा, पुरवठा' },
  waste: { label: 'कचरा/स्वच्छता', icon: '🗑️', hint: 'कचरा संकलन, सार्वजनिक स्वच्छता' },
  health: { label: 'आरोग्य', icon: '🏥', hint: 'आरोग्य सेवा, औषधे, दवाखाना' },
  education: { label: 'शिक्षण', icon: '🎓', hint: 'शाळा, सुविधा, शिक्षक' },
  sanitation: { label: 'स्वच्छता', icon: '🧹', hint: 'शौचालय, स्वच्छता मोहिम' },
  drainage: { label: 'गटार', icon: '🚰', hint: 'निचरा, सांडपाणी, गटार' },
  street_light: { label: 'पथदिवे', icon: '💡', hint: 'रस्त्यावरील दिवे, खांब' },
  noise: { label: 'ध्वनी प्रदूषण', icon: '🔊', hint: 'आवाज, नियमभंग' },
  encroachment: { label: 'अतिक्रमण', icon: '⚠️', hint: 'रस्ता/सार्वजनिक जागा अतिक्रमण' },
  other: { label: 'इतर', icon: '📋', hint: 'इतर नागरी समस्या' },
};

const STATUS_CONFIG = {
  filed: { label: 'नोंदवली', color: '#2563eb', icon: '📋' },
  acknowledged: { label: 'स्वीकृत', color: '#0ea5e9', icon: '✅' },
  assigned: { label: 'नेमणूक', color: '#f59e0b', icon: '👤' },
  in_progress: { label: 'कार्यवाही सुरू', color: '#7c3aed', icon: '🔧' },
  resolved: { label: 'निराकरण', color: '#16a34a', icon: '✓' },
  verified: { label: 'पडताळणी', color: '#059669', icon: '🛡️' },
  closed: { label: 'बंद', color: '#475569', icon: '🔒' },
  reopened: { label: 'पुन्हा उघडली', color: '#dc2626', icon: '↻' },
};

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'कमी', text: 'सामान्य समस्या' },
  { value: 'medium', label: 'मध्यम', text: 'लवकर कार्यवाही' },
  { value: 'high', label: 'उच्च', text: 'तातडीची समस्या' },
  { value: 'critical', label: 'गंभीर', text: 'त्वरित लक्ष आवश्यक' },
];

const STATUS_STEPS = ['filed', 'acknowledged', 'assigned', 'in_progress', 'resolved', 'closed'];
const ACTION_LABELS = {
  created: 'तक्रार नोंदवली',
  status_changed: 'स्थिती बदलली',
  due_date_changed: 'अपेक्षित तारीख बदलली',
  note_added: 'नोंद जोडली',
};
const INITIAL_FORM = { subject: '', category: 'other', severity: 'medium', description: '', location: '', ward_number: '', complainant_name: '', complainant_phone: '' };

function normalizePhone(value = '') {
  return normalizePhoneInput(value);
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatFileSize(bytes = 0) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function formatStatusValue(value) {
  if (!value) return '';
  return STATUS_CONFIG[value]?.label || String(value).replace(/_/g, ' ');
}

function mediaUrl(url) {
  if (!url) return '';
  return url.startsWith('http') ? url : `${API_ORIGIN}${url}`;
}

function getComplaintMedia(complaint) {
  if (!complaint) return [];
  let media = complaint.media_urls || [];
  if (typeof media === 'string') {
    try { media = JSON.parse(media); } catch { media = []; }
  }
  const normalized = Array.isArray(media) ? media : [];
  if (!normalized.length && complaint.photo_before_url) {
    return [{ url: complaint.photo_before_url, type: 'image', originalName: 'तक्रार फोटो' }];
  }
  return normalized;
}

export default function ComplaintsPage() {
  const { tenantSlug } = useParams();
  const { user, isAuthenticated, setUser } = useAuth();
  const [profileUser, setProfileUser] = useState(user);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});
  const [trackId, setTrackId] = useState('');
  const [trackResult, setTrackResult] = useState(null);
  const [trackHistory, setTrackHistory] = useState([]);
  const [trackLoading, setTrackLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    setProfileUser(user);
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let alive = true;
    authAPI.getProfile()
      .then(res => {
        if (!alive || !res.data?.user) return;
        let cachedUser = {};
        try { cachedUser = JSON.parse(localStorage.getItem('user') || '{}'); } catch { cachedUser = {}; }
        const merged = { ...cachedUser, ...res.data.user };
        setProfileUser(merged);
        setUser?.(merged);
        localStorage.setItem('user', JSON.stringify(merged));
      })
      .catch(() => { });
    return () => { alive = false; };
  }, [isAuthenticated, setUser]);

  const accountName = profileUser?.full_name || '';
  const accountPhone = normalizePhone(profileUser?.phone || '');

  useEffect(() => {
    if (!isAuthenticated) return;
    setFormData(prev => ({
      ...prev,
      complainant_name: accountName,
      complainant_phone: accountPhone,
    }));
  }, [isAuthenticated, accountName, accountPhone]);

  const fileInputRef = React.useRef(null);
  const [previews, setPreviews] = useState([]);
  const [activePreviewModal, setActivePreviewModal] = useState(null);

  useEffect(() => {
    const newPreviews = attachments.map(file => {
      const isImg = file.type?.startsWith('image/') || /\.(jpe?g|png|webp|gif|bmp|heic|heif)$/i.test(file.name || '');
      const isVid = file.type?.startsWith('video/') || /\.(mp4|webm|mov|quicktime|m4v|mkv|3gp)$/i.test(file.name || '');
      let url = '';
      try {
        url = URL.createObjectURL(file);
      } catch (e) {
        url = '';
      }
      return {
        file,
        url,
        isImage: isImg,
        isVideo: isVid,
      };
    });
    setPreviews(newPreviews);

    return () => {
      newPreviews.forEach(p => {
        if (p.url) URL.revokeObjectURL(p.url);
      });
    };
  }, [attachments]);

  const selectedCategory = CATEGORY_CONFIG[formData.category] || CATEGORY_CONFIG.other;

  const isImageFile = file => {
    if (!file) return false;
    return file.type?.startsWith('image/') || /\.(jpe?g|png|webp|gif|bmp|heic|heif)$/i.test(file.name || '');
  };

  const isVideoFile = file => {
    if (!file) return false;
    return file.type?.startsWith('video/') || /\.(mp4|webm|mov|quicktime|m4v|mkv|3gp)$/i.test(file.name || '');
  };

  const validateFiles = files => {
    const selected = Array.from(files || []);
    const combined = [...attachments, ...selected];
    if (combined.length > MAX_FILES) {
      return `जास्तीत जास्त ${MAX_FILES} फाइल्स जोडता येतील. (सध्या ${attachments.length} आहेत, कमाल ${MAX_FILES - attachments.length} अधिक जोडता येतील)`;
    }
    const totalSize = combined.reduce((sum, file) => sum + (file.size || 0), 0);
    if (totalSize > TOTAL_LIMIT) return 'सर्व फाइल्सचा एकूण आकार 50MB पेक्षा कमी असावा.';
    for (const file of selected) {
      const isImg = isImageFile(file);
      const isVid = isVideoFile(file);
      if (!isImg && !isVid) return 'फक्त फोटो किंवा व्हिडिओ फाइल्स जोडा (उदा. JPG, PNG, WebP, MP4, MOV).';
      if (isImg && file.size > IMAGE_LIMIT) return `प्रत्येक फोटो 5MB पेक्षा कमी असावा. ("${file.name}" चा आकार ${formatFileSize(file.size)} आहे)`;
      if (isVid && file.size > VIDEO_LIMIT) return `प्रत्येक व्हिडिओ 25MB पेक्षा कमी असावा. ("${file.name}" चा आकार ${formatFileSize(file.size)} आहे)`;
    }
    return '';
  };

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape' && activePreviewModal) {
        setActivePreviewModal(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activePreviewModal]);

  const handleFiles = event => {
    const target = event.target;
    const rawFiles = Array.from(target?.files || []);
    if (target) target.value = '';
    if (!rawFiles.length) return;

    const message = validateFiles(rawFiles);
    if (message) {
      setErrors(prev => ({ ...prev, attachments: message }));
      return;
    }

    setAttachments(prev => [...prev, ...rawFiles]);
    setErrors(prev => ({ ...prev, attachments: '' }));
  };

  const removeAttachment = index => {
    const fileToRemove = attachments[index];
    setAttachments(prev => prev.filter((_, i) => i !== index));
    if (activePreviewModal && fileToRemove && activePreviewModal.file === fileToRemove) {
      setActivePreviewModal(null);
    }
  };

  const handleChange = (field, value) => {
    if (isAuthenticated && ['complainant_name', 'complainant_phone'].includes(field)) return;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!isAuthenticated) nextErrors.auth = 'तक्रार नोंदवण्यासाठी लॉगिन आवश्यक आहे.';
    if (!accountName || accountName.trim().length < 3) nextErrors.complainant_name = 'तुमच्या प्रोफाइलमध्ये पूर्ण नाव आवश्यक आहे.';
    if (!accountPhone || accountPhone.length !== 10) nextErrors.complainant_phone = 'तुमच्या प्रोफाइलमध्ये वैध १० अंकी मोबाइल नंबर आवश्यक आहे.';
    if (!formData.subject.trim() || formData.subject.trim().length < 5) nextErrors.subject = 'शीर्षक किमान ५ अक्षरांचे असावे.';
    if (!formData.description.trim() || formData.description.trim().length < 20) nextErrors.description = 'कृपया समस्येचे किमान २० अक्षरांत वर्णन करा.';
    const fileError = validateFiles([]);
    if (fileError) nextErrors.attachments = fileError;
    return nextErrors;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setSubmitError('');
    setSuccess('');
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    const payload = new FormData();
    payload.append('title', formData.subject.trim());
    payload.append('category', formData.category);
    payload.append('severity', formData.severity);
    payload.append('description', formData.description.trim());
    payload.append('location_address', formData.location.trim());
    payload.append('ward_number', formData.ward_number.trim());
    attachments.forEach(file => payload.append('attachments', file));

    setSubmitting(true);
    try {
      const res = await complaintAPI.create(tenantSlug, payload);
      const reference = res.data?.complaint?.complaint_uid || res.data?.reference;
      setSuccess(`तक्रार यशस्वीपणे नोंदवली. ट्रॅकिंग आयडी: ${reference}`);
      setTrackId(reference || '');
      setFormData({ ...INITIAL_FORM, complainant_name: accountName, complainant_phone: accountPhone });
      setAttachments([]);
      setActivePreviewModal(null);
      setErrors({});
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'तक्रार सादर करता आली नाही. कृपया पुन्हा प्रयत्न करा.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTrack = async event => {
    event.preventDefault();
    setTrackLoading(true);
    setTrackResult(null);
    setTrackHistory([]);
    try {
      const res = await complaintAPI.trackByUid(tenantSlug, trackId.trim());
      setTrackResult(res.data.complaint || 'NOT_FOUND');
      setTrackHistory(res.data.history || []);
    } catch {
      setTrackResult('NOT_FOUND');
    } finally {
      setTrackLoading(false);
    }
  };

  const media = getComplaintMedia(trackResult);
  const status = trackResult && trackResult !== 'NOT_FOUND' ? (STATUS_CONFIG[trackResult.status] || STATUS_CONFIG.filed) : null;
  const currentStep = trackResult && trackResult !== 'NOT_FOUND'
    ? Math.max(0, STATUS_STEPS.indexOf(trackResult.status === 'verified' ? 'closed' : trackResult.status))
    : 0;

  return (
    <div className="complaints-page modern-complaints">
      <section className="complaints-hero">
        <div>
          <span className="hero-kicker">नागरिक तक्रार निवारण</span>
          <h1>तक्रार नोंदवा आणि स्थिती ट्रॅक करा</h1>
          <p>लॉगिन केलेल्या नागरिकांकडूनच तक्रार स्वीकारली जाते. नाव आणि मोबाइल नंबर प्रोफाइलमधून लॉक केले जातात.</p>
        </div>
      </section>

      <div className="complaints-shell">
        <aside className="complaints-side">
          <form className="track-card" onSubmit={handleTrack}>
            <div className="card-title-row">
              <span className="card-icon">🔎</span>
              <div>
                <h3>तक्रार ट्रॅक करा</h3>
                <p>तुमचा तक्रार आयडी टाका.</p>
              </div>
            </div>
            <div className="track-input-wrap">
              <input
                className="track-input"
                value={trackId}
                onChange={e => setTrackId(e.target.value.toUpperCase())}
                placeholder="COM-GP001-20260616-0001"
              />
              <button className="track-btn" disabled={trackLoading || !trackId.trim()}>{trackLoading ? 'शोधत आहे...' : 'ट्रॅक करा'}</button>
            </div>
            {trackResult === 'NOT_FOUND' && <p className="track-not-found">तक्रार सापडली नाही. आयडी तपासून पुन्हा प्रयत्न करा.</p>}
          </form>

          <div className="category-guide-card">
            <div className="card-title-row">
              <span className="card-icon">📂</span>
              <div>
                <h3>तक्रारीचे प्रकार</h3>
                <p>योग्य श्रेणी निवडल्यास कार्यवाही जलद होते.</p>
              </div>
            </div>
            <div className="category-guide-list">
              {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                <div key={key} className="category-guide-info">
                  <span>{cfg.icon}</span>
                  <strong>{cfg.label}</strong>
                  <small>{cfg.hint}</small>
                </div>
              ))}
            </div>
          </div>
        </aside>

        <main className="complaints-main">
          {trackLoading && (
            <div style={{ padding: '32px 0' }}>
              <LoadingSpinner message="तक्रारीची स्थिती शोधत आहे..." />
            </div>
          )}
          {trackResult && trackResult !== 'NOT_FOUND' && (
            <section className="complaint-track-detail">
              <div className="track-detail-hero" style={{ '--status-color': status.color }}>
                <div>
                  <span className="track-id-pill">{trackResult.complaint_uid}</span>
                  <h2>{trackResult.title}</h2>
                  <p>{CATEGORY_CONFIG[trackResult.category]?.icon} {CATEGORY_CONFIG[trackResult.category]?.label || trackResult.category} • नोंदवली {formatDate(trackResult.createdAt || trackResult.created_at)}</p>
                </div>
                <div className="track-card-actions">
                  <span className="status-badge">{status.icon} {status.label}</span>
                  <button type="button" onClick={() => { setTrackResult(null); setTrackHistory([]); }}>× बंद करा</button>
                </div>
              </div>

              <div className="track-progress">
                {STATUS_STEPS.map((step, index) => {
                  const cfg = STATUS_CONFIG[step];
                  const active = index <= currentStep;
                  return (
                    <div key={step} className={active ? 'active' : ''}>
                      <span>{active ? '✓' : index + 1}</span>
                      <strong>{cfg.label}</strong>
                    </div>
                  );
                })}
              </div>

              <div className="track-detail-grid">
                <div className="track-info-card wide">
                  <h3>तक्रारीचा तपशील</h3>
                  <p>{trackResult.description}</p>
                  {trackResult.location_address && <small>📍 {trackResult.location_address}</small>}
                </div>
                <div className="track-info-card">
                  <span>वर्ग</span>
                  <strong>{CATEGORY_CONFIG[trackResult.category]?.icon} {CATEGORY_CONFIG[trackResult.category]?.label || trackResult.category}</strong>
                </div>
                <div className="track-info-card">
                  <span>तीव्रता</span>
                  <strong>{trackResult.severity || 'medium'}</strong>
                </div>
                <div className="track-info-card">
                  <span>नोंदवलेली तारीख</span>
                  <strong>{formatDate(trackResult.createdAt || trackResult.created_at)}</strong>
                </div>
                <div className="track-info-card">
                  <span>अपेक्षित तारीख</span>
                  <strong>{formatDate(trackResult.expected_resolution_date)}</strong>
                </div>
                {trackResult.resolved_at && (
                  <div className="track-info-card">
                    <span>निराकरण तारीख</span>
                    <strong className="resolved-date">✅ {formatDate(trackResult.resolved_at)}</strong>
                  </div>
                )}
                {trackResult.assigned_department && (
                  <div className="track-info-card">
                    <span>विभाग</span>
                    <strong>{trackResult.assigned_department}</strong>
                  </div>
                )}
                {trackResult.assignedOfficer?.full_name && (
                  <div className="track-info-card">
                    <span>अधिकारी</span>
                    <strong>{trackResult.assignedOfficer.full_name}</strong>
                  </div>
                )}
              </div>

              {media.length > 0 && (
                <div className="track-media-card">
                  <h3>जोडलेले पुरावे</h3>
                  <div className="track-media-grid">
                    {media.map((item, index) => (
                      <a key={`${item.url}-${index}`} href={mediaUrl(item.url)} target="_blank" rel="noreferrer" className="track-media-item">
                        {item.type === 'video' || item.mimeType?.startsWith('video/')
                          ? <video src={mediaUrl(item.url)} controls />
                          : <img src={mediaUrl(item.url)} alt={item.originalName || 'तक्रार पुरावा'} />}
                        <span>{item.originalName || `पुरावा ${index + 1}`}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {trackHistory.length > 0 && (
                <div className="timeline-card">
                  <h3>📋 कार्यवाही टाइमलाइन ({trackHistory.length} अपडेट्स)</h3>
                  {trackHistory.map((item, index) => (
                    <div className="timeline-item" key={index}>
                      <span className="timeline-dot" />
                      <div>
                        <div className="timeline-row">
                          <strong>{ACTION_LABELS[item.action] || item.action}</strong>
                          <small>{formatDate(item.created_at || item.createdAt)}</small>
                        </div>
                        <p>by {item.changed_by_name || 'कार्यालय'} {item.changed_by_role && <span>{item.changed_by_role.replace(/_/g, ' ')}</span>}</p>
                        {item.action === 'status_changed' && item.old_value && item.new_value && (
                          <div className="status-change">
                            <b className="old">{formatStatusValue(item.old_value)}</b>
                            <i>→</i>
                            <b className="new">{formatStatusValue(item.new_value)}</b>
                          </div>
                        )}
                        {item.notes && <em>💬 {item.notes}</em>}
                      </div>
                    </div>
                  ))}
                </div>
              )}


              <button type="button" className="secondary-action" onClick={() => { setTrackResult(null); setTrackHistory([]); }}>
                नवीन तक्रार नोंदवा
              </button>
            </section>
          )}

          {!(trackResult && trackResult !== 'NOT_FOUND') && (
            <section className="complaint-form-card">
              <div className="form-card-header">
                <div className="selected-category">
                  <span>{selectedCategory.icon}</span>
                  <div>
                    <small>निवडलेली श्रेणी</small>
                    <strong>{selectedCategory.label}</strong>
                  </div>
                </div>
                <div>
                  <h2>नवीन तक्रार नोंदवा</h2>
                  <p>तुमची ओळख खाते प्रोफाइलमधून घेतली जाते. त्यामुळे नाव आणि मोबाइल बदलता येणार नाही.</p>
                </div>
              </div>

              {!isAuthenticated && (
                <div className="login-required">
                  <strong>लॉगिन आवश्यक आहे</strong>
                  <p>खोटी किंवा अपूर्ण तक्रार टाळण्यासाठी तक्रार फक्त प्रमाणित नागरिक खात्यातून स्वीकारली जाते.</p>
                  <Link to={`/login?gp=${tenantSlug}`}>लॉगिन करा</Link>
                </div>
              )}

              {success && <div className="alert-success">{success}</div>}
              {submitError && <div className="alert-error">{submitError}</div>}
              {errors.auth && <div className="alert-error">{errors.auth}</div>}

              <form className="complaint-form" onSubmit={handleSubmit} noValidate>
                <div className="form-row">
                  <label className={errors.complainant_name ? 'field-error' : ''}>
                    <span>तक्रारदाराचे नाव *</span>
                    <input value={formData.complainant_name} disabled placeholder="प्रोफाइलमधून नाव" />
                    {errors.complainant_name && <small>{errors.complainant_name}</small>}
                  </label>
                  <label className={errors.complainant_phone ? 'field-error' : ''}>
                    <span>मोबाइल नंबर *</span>
                    <input value={formData.complainant_phone} disabled placeholder="प्रोफाइलमधून मोबाइल नंबर" />
                    {errors.complainant_phone && <small>{errors.complainant_phone}</small>}
                  </label>
                </div>

                <div className="form-row">
                  <label>
                    <span>तक्रारीचे प्रकार *</span>
                    <select value={formData.category} onChange={e => handleChange('category', e.target.value)}>
                      {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => <option key={key} value={key}>{cfg.label}</option>)}
                    </select>
                  </label>
                  <label>
                    <span>तीव्रता *</span>
                    <select value={formData.severity} onChange={e => handleChange('severity', e.target.value)}>
                      {SEVERITY_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label} - {option.text}</option>)}
                    </select>
                  </label>
                </div>

                <label className={errors.subject ? 'field-error' : ''}>
                  <span>तक्रारीचे शीर्षक *</span>
                  <input value={formData.subject} onChange={e => handleChange('subject', e.target.value)} placeholder="उदा. मुख्य रस्त्यावर मोठे खड्डे" />
                  {errors.subject && <small>{errors.subject}</small>}
                </label>

                <label className={errors.description ? 'field-error' : ''}>
                  <span>तक्रारीचे वर्णन *</span>
                  <textarea rows="6" value={formData.description} onChange={e => handleChange('description', e.target.value)} placeholder="समस्या कुठे आहे, किती दिवसांपासून आहे आणि नागरिकांना काय त्रास होतो ते लिहा." />
                  <em>{formData.description.length}/20 किमान अक्षरे</em>
                  {errors.description && <small>{errors.description}</small>}
                </label>

                <div className="form-row">
                  <label>
                    <span>ठिकाण / पत्ता</span>
                    <input value={formData.location} onChange={e => handleChange('location', e.target.value)} placeholder="रस्ता, परिसर किंवा जवळची खूण" />
                  </label>
                  <label>
                    <span>वार्ड क्रमांक</span>
                    <input value={formData.ward_number} onChange={e => handleChange('ward_number', e.target.value)} placeholder="उदा. ३" />
                  </label>
                </div>

                <div className={`upload-box ${errors.attachments ? 'field-error' : ''}`}>
                  <div className="upload-box-info">
                    <strong>फोटो / व्हिडिओ पुरावे</strong>
                    <p>ऐच्छिक. फोटो 5MB पर्यंत, व्हिडिओ 25MB पर्यंत. कमाल {MAX_FILES} फाइल्स.</p>
                    {attachments.length > 0 && (
                      <span className="upload-count-badge">
                        जोडलेल्या फाइल्स: {attachments.length} / {MAX_FILES}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="upload-action-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={attachments.length >= MAX_FILES}
                  >
                    <span>📷</span> {attachments.length >= MAX_FILES ? 'कमाल मर्यादा गाठली' : 'फाइल जोडा'}
                  </button>
                  <input
                    ref={fileInputRef}
                    id="complaint-attachments-input"
                    type="file"
                    accept="image/*,video/*,.jpg,.jpeg,.png,.webp,.gif,.mp4,.mov,.webm"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFiles}
                  />
                  {errors.attachments && <small className="upload-error-text">{errors.attachments}</small>}
                </div>

                {previews.length > 0 && (
                  <div className="attachment-preview-section">
                    <div className="preview-section-header">
                      <h4>जोडलेले पुरावे ({previews.length})</h4>
                      <small>फोटो किंवा व्हिडिओवर क्लिक करून मोठा आकार पाहू शकता</small>
                    </div>
                    <div className="attachment-preview-grid">
                      {previews.map((item, index) => (
                        <div key={`${item.file.name}-${index}`} className="attachment-preview-card">
                          <div
                            className="attachment-thumb-container"
                            onClick={() => setActivePreviewModal(item)}
                            title="मोठा आकार पाहण्यासाठी क्लिक करा"
                          >
                            {item.isImage && item.url ? (
                              <img src={item.url} alt={item.file.name} className="attachment-thumb-img" />
                            ) : item.isVideo && item.url ? (
                              <div className="attachment-video-wrapper">
                                <video src={item.url} preload="metadata" className="attachment-thumb-video" />
                                <div className="video-play-indicator">▶</div>
                              </div>
                            ) : (
                              <div className="attachment-generic-icon">
                                <span>{item.isVideo ? '🎬' : '🖼️'}</span>
                              </div>
                            )}
                            {/* <div className="attachment-hover-overlay">
                              <span>🔍 पाहा</span>
                            </div> */}
                          </div>

                          <div className="attachment-card-info">
                            <div className="attachment-meta-row">
                              <span className={`attachment-type-pill ${item.isVideo ? 'video-pill' : 'image-pill'}`}>
                                {item.isVideo ? 'व्हिडिओ' : 'फोटो'}
                              </span>
                              <small className="attachment-size-pill">{formatFileSize(item.file.size)}</small>
                            </div>
                            <strong className="attachment-filename" title={item.file.name}>{item.file.name}</strong>
                            <div className="attachment-actions-row">
                              <button
                                type="button"
                                className="action-btn-preview"
                                onClick={() => setActivePreviewModal(item)}
                              >
                                पाहा
                              </button>
                              <button
                                type="button"
                                className="action-btn-remove"
                                onClick={() => removeAttachment(index)}
                              >
                                काढा
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="form-actions">
                  <span>* आवश्यक माहिती</span>
                  <button type="submit" disabled={submitting || !isAuthenticated}>{submitting ? 'सादर होत आहे...' : 'तक्रार सादर करा →'}</button>
                </div>
              </form>
            </section>
          )}
        </main>
      </div>

      {activePreviewModal && (
        <div
          className="attachment-modal-backdrop"
          onClick={() => setActivePreviewModal(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="attachment-modal-content" onClick={e => e.stopPropagation()}>
            <div className="attachment-modal-header">
              <div className="modal-title-info">
                <span className="modal-type-icon">{activePreviewModal.isVideo ? '🎬' : '🖼️'}</span>
                <div>
                  <h4 title={activePreviewModal.file.name}>{activePreviewModal.file.name}</h4>
                  <small>{formatFileSize(activePreviewModal.file.size)} • {activePreviewModal.isVideo ? 'व्हिडिओ' : 'फोटो'}</small>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setActivePreviewModal(null)}
                aria-label="बंद करा"
              >
                ✕
              </button>
            </div>
            <div className="attachment-modal-body">
              {activePreviewModal.isImage && activePreviewModal.url ? (
                <img
                  src={activePreviewModal.url}
                  alt={activePreviewModal.file.name}
                  className="attachment-full-image"
                />
              ) : activePreviewModal.isVideo && activePreviewModal.url ? (
                <video
                  src={activePreviewModal.url}
                  controls
                  autoPlay
                  className="attachment-full-video"
                />
              ) : (
                <div className="attachment-empty-preview">
                  <p>या फाइलचे पूर्वावलोकन उपलब्ध नाही.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
